// Edwin Maria — Bundled Theme JS
// GSAP core + plugins
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

// Export for use in other modules or inline scripts
window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;

// ── Hero video — suppress on mobile ──
// On mobile: empty the source src and pause so the browser stops downloading.
// The video tag stays in the DOM (CSS hides it) so desktop still works fine.
if (window.matchMedia('(max-width: 760px)').matches) {
  document.querySelectorAll('.hero-video-desktop').forEach((video) => {
    video.pause();
    video.querySelectorAll('source').forEach((s) => { s.removeAttribute('src'); });
    video.load(); // triggers abort of any in-progress download
  });
}

// ── Sticky header + scroll class ──
const header = document.querySelector('.site-header');
if (header) {
  header.classList.add('is-sticky');
  window.addEventListener('scroll', () => {
    header.classList.toggle('is-scrolled', window.scrollY > 20);
  }, { passive: true });
}

// ── Scroll reveal (GSAP) ──
document.body.classList.add('reveal-ready');

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!prefersReducedMotion) {
  // Animate the first .reveal-item (hero) immediately — it is the LCP element
  // and must not be held at opacity:0 waiting for ScrollTrigger to fire.
  const heroReveal = document.querySelector('.reveal-item');
  if (heroReveal) {
    heroReveal.classList.add('reveal-item--skip');
    gsap.set(heroReveal, { opacity: 1, y: 0 });
  }

  // Defer ScrollTrigger.batch to after load — it reads element positions
  // (getBoundingClientRect) which forces a reflow if called during parse.
  // Deferring to load means layout is already stable, no forced reflow.
  window.addEventListener('load', () => {
    ScrollTrigger.batch('.reveal-item:not(.reveal-item--skip)', {
      onEnter: (elements) => {
        gsap.fromTo(elements,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out', stagger: 0.1 }
        );
      },
      once: true,
      start: 'top 92%',
    });
  }, { once: true });
}

// ── FAQ accordion ──
document.querySelectorAll('[data-faq]').forEach((faqSection) => {
  const allowMultiple = faqSection.dataset.faqMulti === 'true';

  faqSection.querySelectorAll('.faq-toggle').forEach((toggle) => {
    toggle.addEventListener('click', function () {
      const item = this.closest('.faq-item');
      const panel = this.nextElementSibling;
      const isOpen = item.classList.contains('is-open');
      const icon = this.querySelector('.faq-toggle-icon');

      // Read all geometry BEFORE any DOM writes to avoid forced reflow
      const panelHeight = isOpen ? null : panel.scrollHeight;
      const closingHeights = [];
      if (!allowMultiple) {
        faqSection.querySelectorAll('.faq-item.is-open').forEach((openItem) => {
          closingHeights.push({ item: openItem, panel: openItem.querySelector('.faq-panel') });
        });
      }

      // Now write
      closingHeights.forEach(({ item: openItem, panel: openPanel }) => {
        openItem.classList.remove('is-open');
        openItem.querySelector('.faq-toggle').setAttribute('aria-expanded', 'false');
        openItem.querySelector('.faq-toggle-icon').textContent = '+';
        openPanel.style.maxHeight = '0';
      });

      if (!isOpen) {
        item.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        icon.textContent = '−';
        panel.style.maxHeight = panelHeight + 'px';
      } else {
        item.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        icon.textContent = '+';
        panel.style.maxHeight = '0';
      }
    });
  });

  // Open first item by default — desktop only
  if (!window.matchMedia('(max-width: 760px)').matches) {
    const firstItem = faqSection.querySelector('.faq-item');
    if (firstItem && !firstItem.classList.contains('is-open')) {
      const firstToggle = firstItem.querySelector('.faq-toggle');
      const firstPanel = firstItem.querySelector('.faq-panel');
      if (firstToggle && firstPanel) {
        const firstPanelHeight = firstPanel.scrollHeight;
        firstItem.classList.add('is-open');
        firstToggle.setAttribute('aria-expanded', 'true');
        firstToggle.querySelector('.faq-toggle-icon').textContent = '−';
        firstPanel.style.maxHeight = firstPanelHeight + 'px';
      }
    }
  }
});

// ── Desktop nav dropdowns (hover — skip on mobile) ──
if (!window.matchMedia('(max-width: 760px)').matches) {
  document.querySelectorAll('.site-nav > ul > li').forEach((item) => {
    let closeTimer;
    item.addEventListener('mouseenter', () => {
      clearTimeout(closeTimer);
      item.classList.add('dropdown-open');
    });
    item.addEventListener('mouseleave', () => {
      closeTimer = setTimeout(() => item.classList.remove('dropdown-open'), 200);
    });
  });
}

// ── Mobile nav hamburger ──
const navToggle = document.querySelector('.nav-toggle');
const navMenu = document.querySelector('.site-nav > ul');

function collapseMobileSubmenus() {
  if (!navMenu) return;

  navMenu.querySelectorAll(':scope > li.submenu-open').forEach((li) => {
    li.classList.remove('submenu-open');
    const chevron = li.querySelector(':scope > .submenu-chevron');
    if (chevron) {
      chevron.setAttribute('aria-expanded', 'false');
      chevron.setAttribute('aria-label', 'Expand submenu');
    }
  });
}

function closeMobileNav() {
  if (!navToggle || !navMenu) return;

  navMenu.classList.remove('is-open');
  navToggle.classList.remove('is-open');
  navToggle.setAttribute('aria-expanded', 'false');
  navToggle.setAttribute('aria-label', 'Open menu');
  collapseMobileSubmenus();
}

if (navToggle && navMenu) {
  navMenu.id = 'primary-nav-list';
  navToggle.setAttribute('aria-controls', 'primary-nav-list');

  navToggle.addEventListener('click', () => {
    const isOpen = navMenu.classList.toggle('is-open');
    navToggle.classList.toggle('is-open', isOpen);
    navToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    navToggle.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
    if (!isOpen) collapseMobileSubmenus();
  });
  document.addEventListener('click', (e) => {
    if (!navToggle.contains(e.target) && !navMenu.contains(e.target)) {
      closeMobileNav();
    }
  });
}

// ── Mobile sub-menu chevrons + phone number ──
// Chevrons are always injected so they are ready if the user resizes to mobile
// or opens DevTools. CSS hides .submenu-chevron on desktop (>760px).
// The phone li is only appended once and is hidden via CSS on desktop.
if (navMenu) {
  document.querySelectorAll('.site-nav > ul > li').forEach((li) => {
    const subMenu = li.querySelector(':scope > ul');
    const parentLink = li.querySelector(':scope > a');
    if (!subMenu || !parentLink) return;

    const submenuId = `${navMenu.id}-submenu-${li.dataset.menuItemId || Array.from(li.parentElement.children).indexOf(li)}`;
    subMenu.id = submenuId;

    const chevron = document.createElement('button');
    chevron.type = 'button';
    chevron.className = 'submenu-chevron';
    chevron.setAttribute('aria-expanded', 'false');
    chevron.setAttribute('aria-controls', submenuId);
    chevron.setAttribute('aria-label', 'Expand submenu');
    chevron.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>';
    parentLink.after(chevron);

    chevron.addEventListener('click', (e) => {
      e.stopPropagation();
      const wasOpen = li.classList.contains('submenu-open');
      collapseMobileSubmenus();
      const isOpen = !wasOpen;
      li.classList.toggle('submenu-open', isOpen);
      chevron.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      chevron.setAttribute('aria-label', isOpen ? 'Collapse submenu' : 'Expand submenu');
    });
  });

  const phoneLi = document.createElement('li');
  phoneLi.className = 'mobile-nav-phone';
  phoneLi.innerHTML = '<a href="tel:+12124703461">(212) 470-3461</a>';
  navMenu.appendChild(phoneLi);
}

// ── Practice card stack (physical deck, scroll-driven) ──
// The cards share one CSS Grid cell. ScrollTrigger only controls their reveal;
// normal document flow remains the fallback on mobile, reduced motion, or no JS.
window.addEventListener('load', function initCardStack() {
  const section = document.querySelector('.practice-cards');
  const inner   = document.querySelector('.card-stack-inner');
  if (!section || !inner) return;

  const cards = Array.from(inner.querySelectorAll('.scroll-stack-card'));
  if (cards.length < 2) return;

  const media = gsap.matchMedia();

  media.add('(min-width: 761px) and (prefers-reduced-motion: no-preference)', () => {
    const PEEK = 44;
    const ENTER_OFFSET = 36;
    let scrollDistance = 0;

    function layoutDeck() {
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const stickyTop = Math.max(24, Math.round(headerHeight + 20));
      const cardHeight = Math.max(...cards.map((card) => card.offsetHeight), 130);
      const deckHeight = cardHeight + ((cards.length - 1) * PEEK);
      const scrollPerCard = Math.max(180, Math.min(280, window.innerHeight * 0.3));

      scrollDistance = Math.round((cards.length - 1) * scrollPerCard);
      inner.style.setProperty('--stack-sticky-top', `${stickyTop}px`);
      inner.style.setProperty('--stack-height', `${deckHeight}px`);
      section.style.minHeight = `${deckHeight + scrollDistance}px`;
    }

    layoutDeck();
    inner.classList.add('is-enhanced');

    cards.forEach((card, index) => {
      card.style.zIndex = String(index + 1);
    });

    // Card zero is the stable base of the deck and must never disappear.
    gsap.set(cards[0], { opacity: 1, y: 0 });
    cards.slice(1).forEach((card, index) => {
      gsap.set(card, { opacity: 0, y: ((index + 1) * PEEK) + ENTER_OFFSET });
    });

    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: () => `top top+=${parseFloat(inner.style.getPropertyValue('--stack-sticky-top')) || 24}`,
        end: () => `+=${scrollDistance}`,
        scrub: 0.6,
        invalidateOnRefresh: true,
        onRefreshInit: layoutDeck,
      },
    });

    cards.slice(1).forEach((card, index) => {
      timeline.to(card, {
        opacity: 1,
        y: (index + 1) * PEEK,
        duration: 1,
        ease: 'none',
      }, index);
    });

    return () => {
      timeline.scrollTrigger.kill();
      timeline.kill();
      inner.classList.remove('is-enhanced');
      inner.style.removeProperty('--stack-sticky-top');
      inner.style.removeProperty('--stack-height');
      section.style.removeProperty('min-height');
      gsap.set(cards, { clearProps: 'opacity,transform,zIndex,willChange' });
    };
  });
}, { once: true });

// ── Tabs ──
document.querySelectorAll('[data-tabs]').forEach((tabContainer) => {
  const btns = Array.from(tabContainer.querySelectorAll('.tab-btn'));
  const panels = tabContainer.querySelectorAll('.tab-panel');

  btns.forEach((btn) => {
    btn.addEventListener('click', function () {
      const target = this.dataset.tab;
      btns.forEach((b) => { b.classList.remove('is-active'); b.setAttribute('aria-selected', 'false'); b.tabIndex = -1; });
      panels.forEach((p) => p.classList.remove('is-active'));
      this.classList.add('is-active');
      this.setAttribute('aria-selected', 'true');
      this.tabIndex = 0;
      const targetPanel = tabContainer.querySelector(`[data-tab-panel="${target}"]`);
      if (targetPanel) targetPanel.classList.add('is-active');
    });
  });

  // Keyboard arrow navigation
  tabContainer.querySelector('.tab-nav')?.addEventListener('keydown', (e) => {
    const idx = btns.indexOf(document.activeElement);
    if (idx === -1) return;
    let next;
    if (e.key === 'ArrowRight') next = btns[(idx + 1) % btns.length];
    else if (e.key === 'ArrowLeft') next = btns[(idx - 1 + btns.length) % btns.length];
    if (next) { e.preventDefault(); next.click(); next.focus(); }
  });

  if (btns.length) btns[0].click();
});

// ── HubSpot form — lazy load on viewport entry ──
// js.hsforms.net/forms/embed/v2.js eagerly loads reCAPTCHA, HubSpot analytics,
// and live chat bundles — ~2.8s of third-party CPU on page load.
// Loading it only when the form scrolls into view defers all of that cost.
const hsWrap = document.querySelector('.hs-form-wrap[data-hs-portal]');
if (hsWrap) {
  // Assign a unique ID so hbspt.forms.create() targets only this element.
  hsWrap.id = 'hs-form-embed';
  new IntersectionObserver((entries, obs) => {
    if (!entries[0].isIntersecting) return;
    obs.disconnect();
    const s = document.createElement('script');
    s.src = '//js.hsforms.net/forms/embed/v2.js';
    s.charset = 'utf-8';
    s.onload = () => {
      if (window.hbspt) {
        hbspt.forms.create({
          region: hsWrap.dataset.hsRegion,
          portalId: hsWrap.dataset.hsPortal,
          formId: hsWrap.dataset.hsForm,
          target: '#hs-form-embed',
        });
      }
    };
    document.head.appendChild(s);
  }, { rootMargin: '200px' }).observe(hsWrap);
}

// ── Mobile sticky bottom CTA bar ──
const stickyCta = document.querySelector('.mobile-sticky-cta');
if (stickyCta && window.matchMedia('(max-width: 760px)').matches) {
  document.body.classList.add('has-sticky-cta');
  window.addEventListener('scroll', () => {
    stickyCta.classList.toggle('is-visible', window.scrollY > 200);
  }, { passive: true });
}

// ── Collapsible footer columns on mobile ──
if (window.matchMedia('(max-width: 760px)').matches) {
  document.querySelectorAll('.site-footer-links, .site-footer-contact').forEach((col) => {
    const heading = col.querySelector('.site-footer-col-heading');
    const list = col.querySelector('ul');
    if (!heading || !list) return;

    const btn = document.createElement('button');
    btn.className = 'footer-col-toggle';
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML = heading.textContent + '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>';
    heading.replaceWith(btn);

    list.style.display = 'none';
    btn.addEventListener('click', () => {
      const isOpen = list.style.display !== 'none';
      list.style.display = isOpen ? 'none' : 'block';
      btn.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
    });
  });
}
